"use client";

import React, { useRef, useEffect } from "react";
import { useRouter } from "next/navigation";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight } from "lucide-react";

// Utility functions for color calculations
const hexToRgb = (hex: string) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
};

const getLuminance = (r: number, g: number, b: number) => {
  const sRGB = [r, g, b].map((c) => {
    c = c / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * sRGB[0] + 0.7152 * sRGB[1] + 0.0722 * sRGB[2];
};

const getTextColor = (backgroundColor: string) => {
  const rgb = hexToRgb(backgroundColor);
  if (!rgb) return 'white';

  const luminance = getLuminance(rgb.r, rgb.g, rgb.b);
  return luminance > 0.5 ? 'black' : 'white';
};

gsap.registerPlugin(ScrollTrigger);

interface NextProjectFooterProps {
  nextProject: {
    id: string;
    title: string;
    category: string;
    accentColor?: string;
  } | null;
}

const NextProjectFooter: React.FC<NextProjectFooterProps> = ({
  nextProject,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    if (!nextProject) return;

    const ctx = gsap.context(() => {
      gsap.fromTo(
        ".footer-content",
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: sectionRef.current,
            start: "top 80%",
          },
        }
      );

      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "center center",
        onEnter: () => {
          gsap.to(".footer-bg", {
            scale: 1.05,
            duration: 0.6,
            ease: "power2.out",
          });
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [nextProject]);

  if (!nextProject) return null;

  const accentColor = nextProject?.accentColor || '#093f71';
  const textColor = getTextColor(accentColor);
  const isLightText = textColor === 'white';

  const handleNextProjectClick = () => {
    gsap.to(sectionRef.current, {
      scale: 1.1,
      duration: 0.8,
      ease: "power2.inOut",
      onComplete: () => {
        router.push(`/projects/${nextProject.id}`);
      },
    });
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden cursor-pointer"
      onClick={handleNextProjectClick}
    >
      {/* Background */}
      <div
        className="footer-bg absolute inset-0 transform scale-100"
        style={{
          background: nextProject?.accentColor
            ? `linear-gradient(to bottom right, ${nextProject.accentColor}, ${nextProject.accentColor}cc)`
            : `linear-gradient(to bottom right, var(--color-accent), var(--color-accent)cc)`
        }}
      />

      {/* Content */}
      <div
        className="footer-content relative z-10 text-center px-4"
        style={{ color: textColor }}
      >
        <div className="max-w-4xl mx-auto">
          {/* Next Project Label */}
          <p
            className="text-lg md:text-xl font-medium mb-4 uppercase tracking-wider font-pp-neuebit"
            style={{ color: isLightText ? 'rgba(255, 255, 255, 0.8)' : 'rgba(0, 0, 0, 0.8)' }}
          >
            Next Project
          </p>

          {/* Project Title */}
          <h2
            className="text-5xl md:text-4xl lg:text-6xl font-bold mb-8 leading-[0.9] tracking-tight font-pp-acma"
            style={{ color: textColor }}
          >
            {nextProject.title}
          </h2>

          {/* Arrow Icon */}
          <div
            className={`relative inline-flex items-center justify-center w-16 h-16 border-2 rounded-full transition-all duration-300 group ${
              isLightText
                ? 'border-white hover:bg-white hover:text-black'
                : 'border-black hover:bg-black hover:text-white'
            }`}
          >
            <ArrowRight className="w-6 h-6 transform group-hover:translate-x-1 transition-transform duration-300" />
          </div>

          {/* CTA text */}
          <p
            className="text-sm md:text-base mt-8 font-pp-neuebit hover:opacity-60 transition-opacity duration-300"
            style={{ color: isLightText ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)' }}
          >
            Next project →
          </p>
        </div>
      </div>
    </section>
  );
};

export default NextProjectFooter;
